import { Bottle, CAPACITY_L, ColorId, GameState, PourPlan, RuleResult, MoveRecord, UndoPatch, PourMove, SealEvent, PackEvent } from "./types";

export function topColor(b: Bottle): ColorId | null {
  return b.layers.length ? b.layers[b.layers.length - 1] : null;
}

export function isMonoColorFull(b: Bottle): boolean {
  if (b.layers.length !== CAPACITY_L) return false;
  const c = b.layers[0];
  return b.layers.every(x => x === c);
}

export function computePourPlan(state: GameState, fromId: number, toId: number): RuleResult<PourPlan> {
  const from = state.bottles.find(b => b.id === fromId);
  const to = state.bottles.find(b => b.id === toId);
  if (!from || !to) return { ok: false, reason: "BottleNotFound" };
  if (state.phase !== "idle" && state.phase !== "selectSource") return { ok: false, reason: "BadPhase" };
  if (from.sealed) return { ok: false, reason: "FromSealed" };
  if (to.sealed) return { ok: false, reason: "ToSealed" };
  if (from.layers.length === 0) return { ok: false, reason: "FromEmpty" };
  if (to.layers.length >= CAPACITY_L) return { ok: false, reason: "ToFull" };

  const c = topColor(from)!;
  const toTop = topColor(to);
  if (to.layers.length !== 0 && toTop !== c) return { ok: false, reason: "ColorMismatch" };

  let run = 0;
  for (let i = from.layers.length - 1; i >= 0; i--) {
    if (from.layers[i] !== c) break;
    run++;
  }
  const free = CAPACITY_L - to.layers.length;
  const amount = Math.min(run, free);
  if (amount <= 0) return { ok: false, reason: "NoOp" };

  return { ok: true, value: { from: fromId, to: toId, color: c, amount } };
}

function createUndoPatch(state: GameState): UndoPatch {
  return {
    bottles: state.bottles.map(b => ({ id: b.id, layers: [...b.layers], sealed: b.sealed, visibility: b.visibility })),
    packingSlots: [...state.packing.slots],
    packingQueue: [...state.packing.queue],
    score: state.score,
    phase: state.phase,
    hiddenBottles: state.hiddenBottles.map(b => ({ id: b.id, layers: [...b.layers], sealed: b.sealed, visibility: b.visibility }))
  };
}

// Helper to bring in hidden bottles when a bottle is packed away
function bringInHiddenBottle(state: GameState, packedBottleId: number): void {
  // Find the packed bottle (which should now be empty)
  const packedBottle = state.bottles.find(b => b.id === packedBottleId);
  if (!packedBottle || packedBottle.layers.length > 0) return;
  
  // Take the first non-empty hidden bottle
  let hiddenBottle: Bottle | undefined = undefined;
  while (state.hiddenBottles.length > 0) {
    const candidate = state.hiddenBottles.shift()!;
    if (candidate.layers.length > 0) {
      hiddenBottle = candidate;
      break;
    }
  }
  if (!hiddenBottle) {
    const packedIndex = state.bottles.findIndex(b => b.id === packedBottleId);
    if (packedIndex !== -1) {
      state.bottles.splice(packedIndex, 1);
    }
    return;
  }
  
  // Replace the packed bottle with the hidden bottle
  // Keep the same ID for cleaner state management
  hiddenBottle.id = packedBottleId;
  
  // Replace in the bottles array
  const index = state.bottles.findIndex(b => b.id === packedBottleId);
  if (index !== -1) {
    state.bottles[index] = hiddenBottle;
  }
}

function applyUndoPatch(state: GameState, patch: UndoPatch): void {
  // Restore hidden bottles if present in patch
  if (patch.hiddenBottles) {
    state.hiddenBottles = patch.hiddenBottles.map(b => ({
      id: b.id,
      layers: [...b.layers],
      sealed: b.sealed,
      visibility: b.visibility
    }));
  }
  
  state.bottles = patch.bottles.map(b => ({
    id: b.id,
    layers: [...b.layers],
    sealed: b.sealed,
    visibility: b.visibility
  }));
  state.packing.slots = [...patch.packingSlots];
  state.packing.queue = [...patch.packingQueue];
  state.score = patch.score;
  state.phase = patch.phase;
}

export function applyPour(state: GameState, plan: PourPlan, enableSealAndPack: boolean = true): MoveRecord {
  const undoPatch = createUndoPatch(state);
  
  const from = state.bottles.find(b => b.id === plan.from)!;
  const to = state.bottles.find(b => b.id === plan.to)!;
  
  for (let i = 0; i < plan.amount; i++) {
    from.layers.pop();
    to.layers.push(plan.color);
  }

  const move: PourMove = { kind: "pour", from: plan.from, to: plan.to, color: plan.color, amount: plan.amount };
  const seals: SealEvent[] = [];
  let pack: PackEvent | undefined = undefined;

  if (enableSealAndPack) {
    // Check affected bottles for sealing
    const affectedBottles = [from, to];
    
    for (const b of affectedBottles) {
      if (isMonoColorFull(b) && !b.sealed) {
        const prevSealed = b.sealed;
        b.sealed = true;
        seals.push({ bottleId: b.id, prevSealed, nextSealed: true });
      }
    }
    
    // Process all sealed bottles that can be packed (chain reaction)
    let packed = true;
    while (packed) {
      packed = false;
      
      for (const b of state.bottles) {
        if (b.sealed && b.layers.length > 0) {
          const color = b.layers[0];
          const slotIndex = state.packing.slots.findIndex(s => s === color);
          
          if (slotIndex !== -1) {
            const prevSlot = state.packing.slots[slotIndex];
            state.packing.slots[slotIndex] = state.packing.queue.shift() ?? null;
            const nextSlot = state.packing.slots[slotIndex];
            const scoreDelta = 1;
            
            // Store bottle ID before clearing
            const packedBottleId = b.id;
            
            b.layers = [];
            b.sealed = false;
            
            // Bring in a hidden bottle to replace the packed one
            bringInHiddenBottle(state, packedBottleId);
            
            // Only record the first pack in moveRecord for animation
            if (!pack) {
              pack = {
                bottleId: b.id,
                color,
                slotIndex,
                prevSlot,
                nextSlot,
                consumedQueue: prevSlot !== null,
                scoreDelta
              };
            }
            
            state.score += scoreDelta;
            packed = true; // Continue checking for more packs
            break; // Break and restart loop since state changed
          }
        }
      }
    }
  }

  const record: MoveRecord = { move, seals, pack, undo: undoPatch };
  state.moves.push(record);
  return record;
}

export function undoMove(state: GameState): boolean {
  if (state.moves.length === 0) return false;
  
  const lastMove = state.moves.pop()!;
  applyUndoPatch(state, lastMove.undo);
  return true;
}

export function checkWin(state: GameState): boolean {
  // Win when all bottles (visible and hidden) are empty
  const visibleEmpty = state.bottles.every(b => b.layers.length === 0);
  const hiddenEmpty = state.hiddenBottles.length === 0 || state.hiddenBottles.every(b => b.layers.length === 0);
  return visibleEmpty && hiddenEmpty;
}

export function getValidMoves(state: GameState): PourPlan[] {
  const moves: PourPlan[] = [];
  const activeBottles = state.bottles.filter(b => !b.sealed);
  
  for (const from of activeBottles) {
    if (from.layers.length === 0) continue;
    
    for (const to of activeBottles) {
      if (from.id === to.id) continue;
      
      const result = computePourPlan(state, from.id, to.id);
      if (result.ok) {
        moves.push(result.value!);
      }
    }
  }
  
  return moves;
}
